import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import Swal from 'sweetalert2';
import {
  DataUpdateRequest,
  DataUpdateRequestItem,
  FacialVerification,
  getItemId,
  getItemPreviewUrl,
  ItemStatus,
  RequestEstado,
  UserForRequest,
} from '../../../interfaces/data-update-request.interface';
import { DataUpdateRequestService } from '../../../services/data-update-request.service';
import { S3UrlService } from 'src/app/shared/services/s3-url.service';
import {
  findPhonePrefix,
  formatDateForDisplay,
  humanizeFieldKey,
  hydrateCurrentDocumentUrls,
  isDateFieldKey,
  PHONE_PREFIX_FIELD_KEY,
  sortItemsByStatus,
} from '../../data-update-request/data-update-request.utils';

@Component({
  selector: 'app-data-update-request-detail',
  templateUrl: './data-update-request-detail.component.html',
  styleUrls: ['./data-update-request-detail.component.sass'],
})
export class DataUpdateRequestDetailComponent implements OnInit {
  loading: boolean = true;
  errorMessage: string | null = null;

  requestId: string = '';
  request: DataUpdateRequest | null = null;

  /**
   * Datos actuales del usuario dueño de la solicitud. Se cargan aparte
   * del `getDetail(id)` porque el response del request trae el estado
   * de cada item por separado pero no el snapshot completo del usuario
   * que el operador necesita para validar contexto ("¿el nombre en el
   * documento coincide con el registrado?", etc.).
   */
  currentUser: UserForRequest | null = null;
  currentUserLoading: boolean = false;

  selectedToApprove = new Set<string>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private service: DataUpdateRequestService,
    private s3UrlService: S3UrlService,
  ) {}

  ngOnInit(): void {
    this.requestId = this.route.snapshot.paramMap.get('id') || '';
    if (!this.requestId) {
      this.errorMessage = 'Solicitud no encontrada.';
      this.loading = false;
      return;
    }
    this.loadDetail();
  }

  async loadDetail(): Promise<void> {
    this.loading = true;
    this.errorMessage = null;
    this.selectedToApprove.clear();
    try {
      const detail = await this.service.getDetail(this.requestId);
      // Ordenamos los items por ciclo de vida antes de renderizar:
      // SUBMITTED (requieren atención del operador) arriba, luego
      // PENDING, REJECTED en reintento, y APPROVED al final como
      // registro histórico.
      this.request = {
        ...detail,
        items: sortItemsByStatus(detail.items || []),
      };
      // Cargamos los datos actuales del usuario en paralelo — no lo
      // hacemos parte del try principal porque si falla, la vista sigue
      // siendo útil (los items y su before/after ya están).
      this.loadCurrentUser(detail.usuarioId);
    } catch (err: any) {
      this.errorMessage =
        err?.error?.message || err?.message || 'No se pudo cargar el detalle.';
    } finally {
      this.loading = false;
    }
  }

  /**
   * Trae los datos actuales del usuario para renderizar en la sección
   * "Datos actuales". Usa `listUsersForRequest` filtrando por usuarioId
   * — es el mismo endpoint que consume el flujo de creación y ya trae
   * el shape completo (`UserForRequest` con campos + keys de docs).
   */
  private async loadCurrentUser(usuarioId: number): Promise<void> {
    if (!usuarioId) return;
    this.currentUserLoading = true;
    try {
      const list = await this.service.listUsersForRequest({ usuarioId, limit: 1 });
      const user = list?.data?.[0] || null;
      if (user) {
        // Firma las keys de documentos (fotoIdfrontal, selfie, ...) si
        // el backend no las devolvió pre-firmadas.
        await hydrateCurrentDocumentUrls(user, this.s3UrlService);
      }
      this.currentUser = user;
    } catch {
      // Silencioso — la vista sigue siendo funcional sin el bloque de
      // "datos actuales". No hay razón para tirar un error si el
      // endpoint no responde.
      this.currentUser = null;
    } finally {
      this.currentUserLoading = false;
    }
  }

  goBack(): void {
    this.router.navigate(['/wallet/admin/data-update-requests']);
  }

  toggleSelect(item: DataUpdateRequestItem): void {
    if (item.status !== 'SUBMITTED') return;
    const key = getItemId(item);
    if (!key) return;
    if (this.selectedToApprove.has(key)) {
      this.selectedToApprove.delete(key);
    } else {
      this.selectedToApprove.add(key);
    }
  }

  /** Helper para el template — evita repetir el fallback id/itemId. */
  itemIdOf(item: DataUpdateRequestItem): string {
    return getItemId(item);
  }

  /** URL de previsualización con fallback previewUrl / fileSignedUrl. */
  itemPreviewUrl(item: DataUpdateRequestItem): string | null {
    return getItemPreviewUrl(item);
  }

  /**
   * Etiqueta del operador que revisó el item (APPROVED / REJECTED).
   * El backend expone ahora `reviewedByEmail` directamente; también
   * aceptamos el legacy `reviewedByCognitoId` (id corto) como fallback
   * para respuestas de endpoints no migrados. `null` si nadie revisó
   * todavía → el chip no se renderiza en el template.
   */
  reviewedByLabel(item: DataUpdateRequestItem): string | null {
    if (item.reviewedByEmail) return item.reviewedByEmail;
    if (item.reviewedByCognitoId) {
      return `#${item.reviewedByCognitoId.slice(0, 8)}`;
    }
    return null;
  }

  /**
   * Renderiza `riskDetail` como texto legible. El contrato lo tipa como
   * `string | null` pero algunas iteraciones del backend lo devuelven
   * como objeto (ej. `{ listName, matchedName, confidence }`). Cuando
   * es objeto, extraemos los campos usuales; si no calzan, caemos a
   * un JSON pretty-print. Sin este helper el template mostraba
   * "[object Object]".
   */
  riskDetailText(request: DataUpdateRequest): string {
    const raw = (request as any).riskDetail;
    if (raw == null || raw === '') {
      return 'Coincidencia en listas PEP/ONU. Revisar manualmente.';
    }
    if (typeof raw === 'string') return raw;
    if (typeof raw === 'object') {
      const parts: string[] = [];
      const listName = raw.listName || raw.list || raw.source;
      const matched = raw.matchedName || raw.name || raw.person;
      const confidence = raw.confidence ?? raw.score;
      const detail = raw.detail || raw.description || raw.reason;
      if (listName) parts.push(`Lista: ${listName}`);
      if (matched) parts.push(`Coincidencia: ${matched}`);
      if (typeof confidence === 'number') {
        const pct = confidence <= 1 ? Math.round(confidence * 100) : Math.round(confidence);
        parts.push(`Confianza: ${pct}%`);
      }
      if (detail) parts.push(String(detail));
      if (parts.length) return parts.join(' · ');
      try {
        return JSON.stringify(raw, null, 2);
      } catch {
        return 'Coincidencia detectada. Revisar manualmente.';
      }
    }
    return String(raw);
  }

  // ── Verificación facial (Rekognition, solo operador) ──────────────

  /**
   * Etiqueta / mensaje principal para el bloque de verificación facial.
   * Se aplican reglas del instructivo:
   *   - Si no se detectó rostro en source (selfie) o target (documento),
   *     mostrar aviso — la comparación no pudo realizarse.
   *   - Si `isMatch`, mostrar OK con la similitud.
   *   - Si no, mostrar warning con la similitud.
   */
  facialVerificationSummary(
    fv: FacialVerification,
  ): { label: string; kind: 'ok' | 'warn' | 'error' } {
    if (!fv.sourceFaceDetected || !fv.targetFaceDetected) {
      return {
        label: 'No se detectó rostro en una de las imágenes',
        kind: 'error',
      };
    }
    const pct = Math.round(fv.similarity);
    if (fv.isMatch) {
      return { label: `Rostro verificado (${pct}%)`, kind: 'ok' };
    }
    return { label: `No coincide (${pct}%)`, kind: 'warn' };
  }

  // ── Bloque "Datos actuales del usuario" ───────────────────────────

  /**
   * Grupos de campos a mostrar en la sección "Datos actuales". Un
   * grupo se oculta completo si ninguno de sus campos tiene valor.
   * Los `fieldKey` acá tienen que coincidir con los campos del
   * `UserForRequest` para que la lectura funcione directo del root.
   */
  private readonly CURRENT_USER_GROUPS: ReadonlyArray<{
    title: string;
    fields: ReadonlyArray<{ key: string; label: string }>;
    companyOnly?: boolean;
  }> = [
    {
      title: 'Identidad',
      fields: [
        { key: 'nombre', label: 'Nombres' },
        { key: 'apellidos', label: 'Apellidos' },
        { key: 'numeroId', label: 'N° documento' },
        { key: 'tipoId', label: 'Tipo documento' },
        { key: 'fechaNacimiento', label: 'Fecha nacimiento' },
        { key: 'sexo', label: 'Sexo' },
        { key: 'nacionalidad', label: 'Nacionalidad' },
      ],
    },
    {
      title: 'Contacto',
      fields: [
        { key: 'email', label: 'Email' },
        { key: 'prefijoTelefono', label: 'Prefijo' },
        { key: 'telefono', label: 'Teléfono' },
      ],
    },
    {
      title: 'Residencia',
      fields: [
        { key: 'paisResidencia', label: 'País' },
        { key: 'ciudadResidencia', label: 'Ciudad' },
        { key: 'direccion', label: 'Dirección' },
        { key: 'profesion', label: 'Profesión' },
      ],
    },
    {
      title: 'Empresa',
      companyOnly: true,
      fields: [
        { key: 'razonSocial', label: 'Razón social' },
        { key: 'rutEmpresa', label: 'RUT empresa' },
        { key: 'nombreComercial', label: 'Nombre comercial' },
        { key: 'domicilioFiscal', label: 'Domicilio fiscal' },
      ],
    },
  ];

  /**
   * Grupos con los valores del usuario ya resueltos. Vuelve a evaluar
   * la lectura en cada llamada del template → cheap porque son pocos
   * campos y evitamos gestionar cache manualmente.
   */
  get currentUserGroups(): Array<{
    title: string;
    entries: Array<{ label: string; value: string }>;
  }> {
    const user = this.currentUser;
    if (!user) return [];
    const isCompany =
      (user.tipoUsuarioId ?? user.tipoUsuarioID) === 1;
    return this.CURRENT_USER_GROUPS.filter((g) => !g.companyOnly || isCompany)
      .map((group) => ({
        title: group.title,
        entries: group.fields
          .map((f) => ({
            label: f.label,
            value: this.formatCurrentUserValue(f.key, this.readUserField(user, f.key)),
          }))
          .filter((e) => e.value && e.value !== '—'),
      }))
      .filter((g) => g.entries.length > 0);
  }

  /**
   * Docs actuales del usuario con URL firmada para preview. Sólo
   * devuelve los que tienen URL disponible.
   */
  get currentUserDocuments(): Array<{ key: string; label: string; url: string }> {
    const user = this.currentUser;
    if (!user) return [];
    const urls = user.currentDocumentUrls || {};
    const catalog: Array<{ key: string; label: string }> = [
      { key: 'fotoIdfrontal', label: 'Frontal del documento' },
      { key: 'fotoIdPosterior', label: 'Reverso del documento' },
      { key: 'selfie', label: 'Selfie del rostro' },
      { key: 'estatuto', label: 'Estatuto' },
      { key: 'documentoIdTributario', label: 'Documento tributario' },
    ];
    const out: Array<{ key: string; label: string; url: string }> = [];
    for (const c of catalog) {
      const url = urls[c.key];
      if (typeof url === 'string' && url.length) {
        out.push({ ...c, url });
      }
    }
    return out;
  }

  /**
   * Lee un campo del `UserForRequest`. Prefiere `currentValues[fieldKey]`
   * si viene poblado; si no, cae al root del user.
   */
  private readUserField(user: UserForRequest, key: string): unknown {
    if (user.currentValues && key in user.currentValues) {
      return user.currentValues[key];
    }
    return (user as any)[key];
  }

  /**
   * Formatea el valor de un campo del usuario para display: fechas al
   * formato `DD/MM/YYYY`, prefijos telefónicos con label del país,
   * booleanos como Sí/No, null/undefined/vacío como `—`.
   */
  private formatCurrentUserValue(key: string, value: unknown): string {
    if (value === null || value === undefined || value === '') return '—';
    if (typeof value === 'boolean') return value ? 'Sí' : 'No';
    if (typeof value === 'string') {
      if (isDateFieldKey(key)) {
        const formatted = formatDateForDisplay(value);
        if (formatted) return formatted;
      }
      if (key === PHONE_PREFIX_FIELD_KEY || key === 'prefijoTelefono') {
        const opt = findPhonePrefix(value);
        if (opt) return `${opt.name} (${opt.code})`;
      }
    }
    return String(value);
  }

  isFile(item: DataUpdateRequestItem): boolean {
    return (
      item.fieldType === 'file' ||
      item.fieldKey === 'fotoIdfrontal' ||
      item.fieldKey === 'fotoIdPosterior' ||
      item.fieldKey === 'selfie'
    );
  }

  /**
   * URL actualmente abierta en el lightbox de imagen (para el modal
   * fullscreen). `null` cuando no hay nada abierto.
   */
  lightboxUrl: string | null = null;

  /** Detecta extensiones de imagen comunes en una URL (incluso con query). */
  isImageUrl(url: string | null | undefined): boolean {
    if (!url) return false;
    return /\.(jpe?g|png|gif|webp|bmp)(\?|$)/i.test(url);
  }

  /** Detecta PDF en una URL (incluso con query string del signed URL). */
  isPdfUrl(url: string | null | undefined): boolean {
    if (!url) return false;
    return /\.pdf(\?|$)/i.test(url);
  }

  openLightbox(url: string): void {
    this.lightboxUrl = url;
    document.body.style.overflow = 'hidden';
  }

  closeLightbox(): void {
    this.lightboxUrl = null;
    document.body.style.overflow = '';
  }

  openExternal(url: string): void {
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  /**
   * Devuelve el label legible del item. Si el backend lo manda en
   * `item.label`, lo respetamos. Sino caemos al diccionario manual
   * (FIELD_LABELS) y por último al algoritmo camelCase → "Camel case".
   */
  labelOf(item: DataUpdateRequestItem): string {
    return item.label || humanizeFieldKey(item.fieldKey);
  }

  get submittedItems(): DataUpdateRequestItem[] {
    return this.request?.items.filter((i) => i.status === 'SUBMITTED') || [];
  }

  get hasSelection(): boolean {
    return this.selectedToApprove.size > 0;
  }

  async approveSelected(): Promise<void> {
    if (!this.request || !this.hasSelection) return;
    const itemIds = Array.from(this.selectedToApprove);
    const confirm = await Swal.fire({
      icon: 'question',
      title: `¿Aprobar ${itemIds.length} item(s)?`,
      text: 'Los datos se aplicarán al perfil del cliente.',
      showCancelButton: true,
      confirmButtonText: 'Aprobar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#1e5a3f',
    });
    if (!confirm.isConfirmed) return;
    try {
      await this.service.approveItems(this.request.id, { itemIds });
      await this.loadDetail();
    } catch (err: any) {
      Swal.fire({
        icon: 'error',
        title: 'No se pudo aprobar',
        text: err?.error?.message || err?.message || '',
        confirmButtonColor: '#1e5a3f',
      });
    }
  }

  async rejectItem(item: DataUpdateRequestItem): Promise<void> {
    if (!this.request) return;
    const reasonRes = await Swal.fire({
      title: 'Motivo del rechazo',
      input: 'textarea',
      inputPlaceholder: 'Explicá por qué se rechaza este dato...',
      showCancelButton: true,
      confirmButtonText: 'Continuar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#1e5a3f',
      inputValidator: (v) => (!v || !v.trim() ? 'El motivo es obligatorio' : null),
    });
    if (!reasonRes.isConfirmed || !reasonRes.value) return;

    // SweetAlert2 no tipa `input: 'date'` (sólo text/email/etc.), pero el
    // DOM nativo sí lo soporta. Usamos `input: 'text'` + `inputAttributes`
    // para que el browser renderice el datepicker.
    const dateRes = await Swal.fire({
      title: 'Nuevo plazo',
      input: 'text',
      inputAttributes: {
        type: 'date',
        min: new Date().toISOString().slice(0, 10),
      },
      inputValue: this.defaultDeadline(),
      showCancelButton: true,
      confirmButtonText: 'Rechazar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#dc2626',
    });
    if (!dateRes.isConfirmed || !dateRes.value) return;

    try {
      await this.service.rejectItems(this.request.id, {
        items: [{ itemId: getItemId(item), reason: reasonRes.value }],
        newDeadlineAt: this.normalizeDeadline(dateRes.value),
      });
      await this.loadDetail();
    } catch (err: any) {
      Swal.fire({
        icon: 'error',
        title: 'No se pudo rechazar',
        text: err?.error?.message || err?.message || '',
        confirmButtonColor: '#1e5a3f',
      });
    }
  }

  async cancelRequest(): Promise<void> {
    if (!this.request) return;
    const confirm = await Swal.fire({
      icon: 'warning',
      title: '¿Cancelar la solicitud?',
      text: 'Esta acción no se puede deshacer.',
      showCancelButton: true,
      confirmButtonText: 'Cancelar solicitud',
      cancelButtonText: 'Volver',
      confirmButtonColor: '#dc2626',
    });
    if (!confirm.isConfirmed) return;
    try {
      await this.service.cancel(this.request.id);
      this.goBack();
    } catch (err: any) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err?.error?.message || err?.message || '',
        confirmButtonColor: '#1e5a3f',
      });
    }
  }

  // ── Helpers de presentación ──────────────────────────────────────

  statusColor(status: ItemStatus): string {
    switch (status) {
      case 'PENDING':
        return '#f59e0b';
      case 'SUBMITTED':
        return '#3b82f6';
      case 'APPROVED':
        return '#16a34a';
      case 'REJECTED':
        return '#dc2626';
      default:
        return '#6b7280';
    }
  }

  statusLabel(status: ItemStatus): string {
    switch (status) {
      case 'PENDING':
        return 'Pendiente';
      case 'SUBMITTED':
        return 'En revisión';
      case 'APPROVED':
        return 'Aprobado';
      case 'REJECTED':
        return 'Rechazado';
      default:
        return status;
    }
  }

  estadoLabel(estado: RequestEstado): string {
    switch (estado) {
      case 'PENDING':
        return 'Pendiente';
      case 'COMPLETED':
        return 'Completada';
      case 'EXPIRED':
        return 'Vencida';
      case 'CANCELLED':
        return 'Cancelada';
      case 'CLOSED_MAX_REJECTS':
        return 'Cerrada (máx reintentos)';
      default:
        return estado;
    }
  }

  numeroIdMatchLabel(match?: string | null): string | null {
    if (!match || match === 'none') return null;
    if (match === 'usuario') return 'Ya existe en usuarios';
    if (match === 'pagador') return 'Ya existe en pagadores';
    return null;
  }

  /**
   * Texto explicativo para el flag "Ya existe en usuarios/pagadores".
   * El flag por sí solo no le decía al operador por qué era relevante
   * ni qué acción tomar — este helper le da contexto.
   */
  numeroIdMatchExplanation(match?: string | null): string | null {
    if (match === 'usuario') {
      return 'El nuevo número de documento coincide con otro usuario ya registrado. Verificá si es un duplicado antes de aprobar.';
    }
    if (match === 'pagador') {
      return 'El nuevo número de documento coincide con un pagador ya registrado. Confirmá que corresponde al mismo titular.';
    }
    return null;
  }

  /**
   * Explicación del flag `identityChange`. Cuando podemos, listamos
   * los campos del OCR que difieren de los datos actuales del item o
   * la solicitud — le da al operador contexto concreto para revisar.
   */
  identityChangeExplanation(item: DataUpdateRequestItem): string {
    const diffs = this.identityChangeDiffs(item);
    if (diffs.length) {
      return `El OCR detectó diferencias en: ${diffs.join(', ')}. Verificá que el documento sea del mismo titular antes de aprobar.`;
    }
    return 'El OCR extrajo datos distintos a los actuales del usuario. Verificá el documento antes de aprobar.';
  }

  /**
   * Compara `ocrExtracted` (nombres/apellidos/numeroId/fechaNacimiento)
   * contra el `currentValue` del item cuando aplica, o contra los datos
   * actuales del request. Devuelve los nombres de campo con diferencias.
   */
  private identityChangeDiffs(item: DataUpdateRequestItem): string[] {
    const ocr = item.ocrExtracted;
    if (!ocr) return [];
    const labels: Record<string, string> = {
      nombres: 'Nombres',
      apellidos: 'Apellidos',
      numeroId: 'N° ID',
      fechaNacimiento: 'Fecha nac.',
    };
    const diffs: string[] = [];
    const current = String(item.currentValue ?? '').trim().toLowerCase();
    for (const [key, label] of Object.entries(labels)) {
      const ocrVal = String((ocr as any)[key] ?? '').trim().toLowerCase();
      if (!ocrVal) continue;
      // El item sólo trae `currentValue` para su fieldKey. Sólo
      // comparamos cuando el fieldKey del item coincide con el campo
      // OCR — evita falsos positivos con datos que no están en el item.
      if (item.fieldKey === key && current && current !== ocrVal) {
        diffs.push(label);
      }
    }
    return diffs;
  }

  /**
   * Formatea un valor de item para el before/after. Recibe `fieldKey`
   * opcional para tratar fechas (ISO → DD/MM/YYYY) y prefijos
   * telefónicos ("+56" → "Chile (+56)").
   */
  formatValue(v: unknown, fieldKey?: string): string {
    if (v === null || v === undefined || v === '') return '—';
    if (typeof v === 'boolean') return v ? 'Sí' : 'No';
    if (fieldKey && typeof v === 'string') {
      if (isDateFieldKey(fieldKey)) {
        const formatted = formatDateForDisplay(v);
        if (formatted) return formatted;
      }
      if (fieldKey === PHONE_PREFIX_FIELD_KEY) {
        const opt = findPhonePrefix(v);
        if (opt) return `${opt.name} (${opt.code})`;
      }
    }
    return String(v);
  }

  private defaultDeadline(): string {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return d.toISOString().slice(0, 10);
  }

  private normalizeDeadline(value: string): string {
    return new Date(`${value}T23:59:00`).toISOString();
  }
}
